import { Button } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ModulesOptionsDataFragment } from '../../../api/fragments/ModulesOptionsData';
import {
    ImportFinderVehiclesLtaDocument,
    type ImportFinderVehiclesLtaMutation,
    type ImportFinderVehiclesLtaMutationVariables,
} from '../../../api/mutations/importFinderVehiclesLta';
import { ListFinderVehiclesDocument } from '../../../api/queries/listFinderVehicles';
import { ImportStatus, ModuleType } from '../../../api/types';
import useImportModal from '../../../components/importExport/useImportModal';
import useModuleSelectionModal from '../../../components/importExport/useModuleSelectionModal';
import { exportLtaTemplate } from '../../../utilities/export';

type ImportLtaButtonProps = {
    availableModules?: ModulesOptionsDataFragment[];
};

const ImportLtaButton = ({ availableModules }: ImportLtaButtonProps) => {
    const { t } = useTranslation(['finderVehicleList', 'importExportExcel']);
    const [isOpen, setOpen] = useState(false);

    const { shouldShowModuleSelection, initialModuleId } = useMemo(
        () => ({
            shouldShowModuleSelection: availableModules?.length > 1,
            initialModuleId: availableModules?.[0]?.id,
        }),
        [availableModules]
    );

    const moduleSelectionModal = useModuleSelectionModal({
        title: t('finderVehicleList:modal.importLTA'),
        moduleType: ModuleType.FinderVehicleManagement,
        onFinish: moduleId => {
            if (moduleId) {
                setOpen(true);
            }
        },
        availableModules,
    });

    const importModal = useImportModal<ImportFinderVehiclesLtaMutation, ImportFinderVehiclesLtaMutationVariables>({
        isOpen,
        setOpen,
        downloadTemplate: exportLtaTemplate,
        getVariables: (file: File) => ({
            moduleId: shouldShowModuleSelection ? moduleSelectionModal.selectedModuleId : initialModuleId,
            upload: file,
        }),
        refetchQueries: [ListFinderVehiclesDocument],
        title: t('finderVehicleList:modal.importLTA'),
        uploadMutation: ImportFinderVehiclesLtaDocument,
        onSuccess: (data, { setUpdatedCount, setError }) => {
            switch (data.response.status) {
                case ImportStatus.Success:
                    setUpdatedCount(data.response.updatedCount);
                    break;

                case ImportStatus.Fail:
                    setError(data.response.message);
                    break;

                default:
                    throw new Error('Invalid response type');
            }
        },
        downloadSuccessMessage: t('finderVehicleList:messages.successDownloadTemplate'),
        updatedCountTransKey: 'finderVehicleList:messages.importLtaSuccess',
    });

    const onButtonClick = useCallback(() => {
        if (shouldShowModuleSelection) {
            moduleSelectionModal.setOpenModal(true);
        } else {
            setOpen(true);
        }
    }, [moduleSelectionModal, shouldShowModuleSelection]);

    // Ensure at least there is one finder vehicle modules, before render
    if (!initialModuleId) {
        return null;
    }

    return (
        <>
            {shouldShowModuleSelection && moduleSelectionModal.render()}
            {importModal.render()}
            <Button loading={importModal.isSubmitting} onClick={onButtonClick} type="default">
                {t('finderVehicleList:actions.importLTA')}
            </Button>
        </>
    );
};

export default ImportLtaButton;
